import React, { useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import i18n from '../../i18n';
import { accountState } from '../my-account/account-state';
import { Box, ListItemText } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import ScrollTabs from '../../component/tab/scroll-tabs.component';
import PeriodDateFilter from '../../component/period-date-filter.component';
import GroupTotalRevenue from './group-total-revenue.component';
import MultiStoreComparison from './multi-store-comparison.component';
import GroupDishSalesAnalysis from './group-dish-sales-analysis.component';
import groupRevenueAnalysisState from './group-revenue-analysis-state';
import PrefixLabeledPillSelect from '../../component/prefix-labeled-pill-select.component';
import { ReactComponent as PillSelectStoreIcon } from '../../static/images/svg/bxs_store.svg';

/** Tab 配置：集团营业总额、多门店数据对比、集团菜品销售分析（title 为 i18n key） */
const TAB_KEYS = [
    'group_revenue_analysis_tab_total',
    'group_revenue_analysis_tab_multi_store',
    'group_revenue_analysis_tab_dish_sales'
];

/**
 * 视口宽度 ≥ 此值时 Tab 与「集团 + 周期/自定义日期」同排；
 * 低于此值改为双行顶栏，避免选自定义日期时整行换行（与 theme.breakpoints.values.xl 一致）。
 */
const GROUP_REVENUE_ANALYSIS_HEADER_SINGLE_ROW_MIN_VIEWPORT_PX = 1600;

/**
 * 集团营收分析页面
 * 三个 Tab 对应集团营业总额、多门店数据对比、集团菜品销售分析；顶部时间筛选联动三个接口。
 */
const GroupRevenueAnalysisPage: React.FC = observer(() => {
    const state = groupRevenueAnalysisState;
    const groups = accountState.userInfo?.responsible_group ?? [];
    /** 视口是否足以单行展示 Tab + 集团选择 + 周期/自定义日期 */
    const fitsHeaderSingleRow = useMediaQuery(
        `(min-width: ${GROUP_REVENUE_ANALYSIS_HEADER_SINGLE_ROW_MIN_VIEWPORT_PX}px)`,
    );
    const firstGroupId = groups[0]?.string_id ?? '';
    /** 与 state 同步后始终以 selectedGroupStringId 为准；首屏未写入前用第一项兜底 */
    const groupId = state.selectedGroupStringId || firstGroupId;

    const handleChangeTab = useCallback((index: number) => {
        state.setActiveTabIndex(index);
    }, []);

    /** 无负责集团时清空已选，避免子组件仍使用过期的 string_id */
    useEffect(() => {
        if (groups.length === 0) {
            state.setSelectedGroupStringId('');
        }
    }, [groups.length, state]);

    /**
     * 集团选择同步：未选时把第一项写入 selectedGroupStringId；
     * 已选但不在负责列表中则清空，下一轮 effect 会再落到第一项。
     */
    useEffect(() => {
        if (!groups.length) return;
        const sid = state.selectedGroupStringId;
        if (sid && !groups.some((g) => g.string_id === sid)) {
            state.setSelectedGroupStringId('');
            return;
        }
        if (!sid && firstGroupId) {
            state.setSelectedGroupStringId(firstGroupId);
        }
    }, [groups, state.selectedGroupStringId, firstGroupId]);

    /** 时间范围变更：仅刷新当前 Tab 对应接口（Tab0/1 仪表盘+趋势；Tab2 菜单销售由子组件内 effect 拉数） */
    const handleDateTimeChange = useCallback(
        (dates: [Date | null, Date | null]) => {
            state.handleDateTime(dates);
            if (!dates[0] || !dates[1] || !groupId) return;
            if (state.activeTabIndex === 0) void state.fetchDashboardTrendForTab0(groupId);
            else if (state.activeTabIndex === 1) void state.fetchDashboardTrendForTab1(groupId);
        },
        [groupId, state.activeTabIndex]
    );

    /** 集团或 Tab 切换：Tab0 / Tab1 各自请求对应仪表盘+趋势；Tab2 在子组件内拉取 */
    useEffect(() => {
        if (!groupId) return;
        const tab = state.activeTabIndex;
        if (tab === 0) void state.fetchDashboardTrendForTab0(groupId);
        else if (tab === 1) void state.fetchDashboardTrendForTab1(groupId);
    }, [groupId, state.activeTabIndex]);

    /** 切换集团：清空对比筛选、清空 Tab3 菜单销售缓存；仪表盘请求由上方 effect 按当前 Tab 触发 */
    const handleGroupChange = useCallback(
        (id: string) => {
            if (!id) return;
            state.setSelectedGroupStringId(id);
            state.resetMultiStoreSelection();
            state.resetDishSalesTabFilters();
        },
        [state]
    );

    const renderTabContent = () => {
        switch (state.activeTabIndex) {
            case 0:
                return <GroupTotalRevenue />;
            case 1:
                return <MultiStoreComparison />;
            case 2:
                return <GroupDishSalesAnalysis />;
            default:
                return null;
        }
    };

    /** 未分配负责集团：仅提示无权限，不展示 Tab 与右侧集团/时间筛选 */
    if (groups.length === 0) {
        return (
            <Box
                sx={{
                    minHeight: 'calc(100vh - 160px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                }}
            >
                <ListItemText
                    primary={i18n.t('group_revenue_analysis_text_no_permission')}
                    primaryTypographyProps={{
                        variant: 'h4',
                        color: 'var(--text-dark)',
                        textAlign: 'center',
                    }}
                />
            </Box>
        );
    }

    /** 集团 + 周期/自定义日期筛选条（单行与双行顶栏共用；宽度不足时自动换行） */
    const headerFilterToolbar = (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 2,
                rowGap: 2,
                pr: 2,
                flex: '1 1 auto',
                minWidth: 0,
                flexWrap: 'wrap',
            }}
        >
            <PrefixLabeledPillSelect
                options={groups.map((g) => ({
                    value: g.string_id ?? '',
                    label: g.nickname ?? g.string_id ?? '',
                }))}
                value={groupId}
                onChange={handleGroupChange}
                labelPrefix={i18n.t('group_revenue_analysis_selector_label_prefix')}
                leadingIcon={<PillSelectStoreIcon style={{ width: 32, height: 32 }} />}
            />
            <PeriodDateFilter
                startDate={state.startDate}
                endDate={state.endDate}
                pickerValue={state.pickerValue}
                onDateTimeChange={handleDateTimeChange}
            />
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, height: '100%', minHeight: 0 }}>
            {fitsHeaderSingleRow ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 2,
                        mb: 2,
                        flexShrink: 0,
                        minWidth: 0,
                    }}
                >
                    <Box sx={{ minWidth: 0, flexShrink: 0 }}>
                        <ScrollTabs
                            data={TAB_KEYS.map((key) => ({ title: i18n.t(key) }))}
                            activeIndex={state.activeTabIndex}
                            onClick={handleChangeTab}
                        />
                    </Box>
                    {headerFilterToolbar}
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2, flexShrink: 0 }}>
                    <Box sx={{ minWidth: 0 }}>
                        <ScrollTabs
                            data={TAB_KEYS.map((key) => ({ title: i18n.t(key) }))}
                            activeIndex={state.activeTabIndex}
                            onClick={handleChangeTab}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', minWidth: 0 }}>
                        {headerFilterToolbar}
                    </Box>
                </Box>
            )}
            {/* 内容区：占满顶栏下方剩余高度；小屏内容过高时滚动，大屏 Tab 内 flex 分配避免多余滚动条 */}
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {renderTabContent()}
            </Box>
        </Box>
    );
});

export default GroupRevenueAnalysisPage;
